import type { ReactNode } from "react";
import { WifiSlash } from "@phosphor-icons/react";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { cn } from "@/lib/utils";

export interface OfflineBannerProps {
  /** Optional className for layout composition. */
  className?: string;
}

/**
 * Sticky banner shown when the browser reports offline. Placed inside
 * `AppLayout` AFTER `<SkipLink />` so the skip-link remains first focusable.
 *
 * Spec: docs/architecture/states-pattern.md §2.4 / §6.
 */
export function OfflineBanner({ className }: OfflineBannerProps = {}): ReactNode {
  const online = useOnlineStatus();
  if (online) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      data-state="offline"
      data-testid="offline-banner"
      className={cn(
        "sticky top-0 z-40 flex items-center justify-center gap-2 bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground shadow",
        className,
      )}
    >
      <WifiSlash aria-hidden="true" className="h-4 w-4" weight="bold" />
      <span>Você está offline. Algumas ações podem não funcionar até a conexão voltar.</span>
    </div>
  );
}
